import React, { useState, SyntheticEvent, Fragment, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { Lock, Mail, MailCheck, UserPen, UserRoundCheck } from "lucide-react";

import { AppConfig } from "../../config/app.config";

import { LogoPackageHoriz } from "../sub/LogoPackage";
import { TextInput } from "../shared/Inputs";

import { useCreateGuestSession, useResetPassword, useSendEmailVerificationEmail, useSendPasswordResetEmail, useSetPassword, useSignIn, useSignup, useVerifyEmail } from "../../hooks/auth-ux";

import { CookiesHelper } from "../../helpers/cookies.helper";
import { AuthHelper } from "../../helpers/auth.helper";
import { getWhiteLabelingClient } from "../../helpers/misc.helper";

import { EWhiteLabelingClient } from "../../types/misc.enum";

const isGuestSessionAllowed = (): boolean => {
    return (
        getWhiteLabelingClient() === EWhiteLabelingClient.MINDSHIELD ||
        getWhiteLabelingClient() === EWhiteLabelingClient.RIDLEY_CONSULTING
    ) && !AuthHelper.isGuestTokenValid().result;
};

export const Auth = () => {
    const { view } = useParams();
    const navigate = useNavigate();

    useEffect(() => {
        if (view !== 'sign-in' && view !== 'forget-password' && view !== 'reset-password' && view !== 'set-password') {
            navigate('/auth/sign-in', { replace: true });
        };

        // eslint-disable-next-line
    }, [view]);

    return (
        <div className="auth__page">
            <div className="auth__content">
                <div className="auth__content__left">
                    <LogoPackageHoriz />
                    {view === 'sign-in' && <SignInView />}
                    {view === 'sign-up' && <SignUpView />} {/* NOTE: This view is not used in the app */}
                    {view === 'verify-email' && <VerifyEmailView />} {/* NOTE: This view is not used in the app */}
                    {view === 'forget-password' && <ForgotPasswordView />}
                    {view === 'reset-password' && <ResetPasswordView />}
                    {view === 'set-password' && <SetPasswordView />}
                </div>
                <div className="auth__content__right">
                    <img src="/assets/auth_bg.png" alt="" className="auth__content__right__bg_img" />
                </div>
            </div>
        </div>
    );
};

const SignInView = () => {
    const navigate = useNavigate();

    const [guestProcessing, setGuestProcessing] = useState<boolean>(false);
    const [guestError, setGuestError] = useState<string | null>(null);
    const [email, setEmail] = useState<string>('');
    const [username, setUsername] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const [emailOrUsername, setEmailOrUsername] = useState<'email' | 'username'>('email');
    const [processing, setProcessing] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [showResendVerification, setShowResendVerification] = useState<boolean>(false);
    const [sendVerificationProcessing, setSendVerificationProcessing] = useState<boolean>(false);
    const [sendVerificationError, setSendVerificationError] = useState<string | null>(null);
    const [sendVerificationSuccess, setSendVerificationSuccess] = useState<boolean>(false);

    const { mutate: createGuestSession } = useCreateGuestSession();
    const { mutate: signIn } = useSignIn();
    const { mutate: sendEmailVerificationEmail } = useSendEmailVerificationEmail();

    const isSubmitDisabled = (): boolean => {
        if (
            !password?.trim()?.length ||
            (emailOrUsername === "email" && !email?.trim()?.length) ||
            (emailOrUsername === "username" && !username?.trim()?.length)
        ) {
            return true;
        };

        return false;
    };

    const handleSubmit = (e: SyntheticEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (isSubmitDisabled() || processing || guestProcessing || sendVerificationProcessing) return;

        let tokenOf: 'session' | 'guest' = 'guest';
        let token = CookiesHelper.get(AppConfig.cookieTokenNames.guest);
        if (!token) {
            token = CookiesHelper.get(AppConfig.cookieTokenNames.session);
            tokenOf = 'session';
        };
        if (!token) return;

        setSendVerificationError(null);
        setSendVerificationSuccess(false);

        signIn({
            payload: {
                email: email?.trim()?.length && emailOrUsername === 'email' ? email : null,
                username: username?.trim()?.length && emailOrUsername === 'username' ? username : null,
                password,
                tokenOf,
            },
            token,
            setProcessing,
            setError,
            setShowResendVerification,
        });
    };

    const handleSwitchEmailOrUsername = () => {
        if (processing || guestProcessing || sendVerificationProcessing) return;
        setEmailOrUsername(emailOrUsername === 'email' ? 'username' : 'email');
    };

    // const goToSignUp = () => {
    //     if (processing || guestProcessing || sendVerificationProcessing) return;
    //     navigate('/auth/sign-up');
    // };

    const goToForgotPassword = () => {
        if (processing || guestProcessing || sendVerificationProcessing) return;
        navigate('/auth/forget-password');
    };

    const handleGuestProceed = () => {
        if (!isGuestSessionAllowed()) return;

        createGuestSession({
            setProcessing: setGuestProcessing,
            setError: setGuestError,
        });
    };

    const handleResendVerification = () => {
        if (processing || guestProcessing || sendVerificationProcessing) return;

        let tokenOf: 'session' | 'guest' = 'guest';
        let token = CookiesHelper.get(AppConfig.cookieTokenNames.guest);
        if (!token) {
            token = CookiesHelper.get(AppConfig.cookieTokenNames.session);
            tokenOf = 'session';
        };
        if (!token) return;

        sendEmailVerificationEmail({
            payload: {
                email,
                tokenOf,
            },
            token,
            setProcessing: setSendVerificationProcessing,
            setError: setSendVerificationError,
            setSuccess: setSendVerificationSuccess,
        });
    };

    const handleGoToSignInFromSentVerification = () => {
        setSendVerificationSuccess(false);
        setShowResendVerification(false);
        setError(null);
    };

    return (
        <div className="auth__view__sign_in">
            {!sendVerificationSuccess
                ? <Fragment>
                    <p className="text-4xl text-bold color--primary">Welcome!</p>
                    <p className="text-lg color--txt-secondary mt-lg">Login in continue your cognitive security journey</p>
                    <div className="auth__view__sign_in__social_btns">
                        <button className="btn btn--lg btn--radius-md btn--fluid btn--secondary" disabled>
                            <img src="/assets/google-icon.svg" alt="Google Ico" />
                            <span>Continue with Google</span>
                        </button>
                        <div className="auth__view__sign_in__social_btns__separator">
                            <div /><p>or</p><div />
                        </div>
                    </div>
                    <form className="auth__view__sign_in__form" onSubmit={handleSubmit}>
                        {emailOrUsername === 'email' &&
                            <Fragment>
                                <div className="auth__view__sign_in__form__form_group">
                                    <label htmlFor="sign_in__email">
                                        <p className="text-sm text-medium color--primary">Email Address</p>
                                    </label>
                                    <TextInput
                                        id="sign_in__email"
                                        type="email"
                                        icon={{ icon: <Mail />, position: 'prefix' }}
                                        placeholder="Enter your email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                    />
                                </div>
                                <p className="text-sm text-right color--txt-tertiary mt-xs">
                                    Want to use username?&nbsp;
                                    <button type="button" className="link link--primary text-sm" onClick={handleSwitchEmailOrUsername}>Click here</button>
                                </p>
                            </Fragment>
                        }
                        {emailOrUsername === 'username' &&
                            <Fragment>
                                <div className="auth__view__sign_in__form__form_group">
                                    <label htmlFor="sign_in__username">
                                        <p className="text-sm text-medium color--primary">Username</p>
                                    </label>
                                    <TextInput
                                        id="sign_in__username"
                                        type="text"
                                        icon={{ icon: <UserPen />, position: 'prefix' }}
                                        placeholder="Enter your username"
                                        value={username}
                                        onChange={(e) => setUsername(e.target.value)}
                                    />
                                </div>
                                <p className="text-sm text-right color--txt-tertiary mt-xs">
                                    Want to use email?&nbsp;
                                    <button type="button" className="link link--primary text-sm" onClick={handleSwitchEmailOrUsername}>Switch to email</button>
                                </p>
                            </Fragment>
                        }
                        <div className="auth__view__sign_in__form__form_group my-lg">
                            <label htmlFor="sign_in__password">
                                <p className="text-sm text-medium color--primary">Password</p>
                            </label>
                            <TextInput
                                id="sign_in__password"
                                type="password"
                                icon={{ icon: <Lock />, position: 'prefix' }}
                                placeholder="Enter your password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                            />
                        </div>
                        <div className="auth__view__sign_in__form__forgot_password_btn__container">
                            <button
                                type="button"
                                className="link link--primary"
                                onClick={goToForgotPassword}
                            >
                                Forgot password?
                            </button>
                        </div>
                        <button
                            type="submit"
                            className="btn btn--lg btn--radius-sm btn--fluid btn--primary mt-xl"
                            disabled={isSubmitDisabled()}
                        >
                            {processing ? <span className="btn-loader" /> : "Sign In"}
                        </button>
                        {error && <p className="text-sm text-medium color--error text-center mt-xs">{error}</p>}
                    </form>
                    {showResendVerification &&
                        <Fragment>
                            <p className="text-center color--txt-tertiary mt-sm">
                                Want to verify email?&nbsp;
                                <button className="link link--primary" onClick={handleResendVerification}>
                                    {processing ? "Sending Email..." : "Resend Verification Link"}
                                </button>
                            </p>
                            {sendVerificationError && <p className="text-sm text-medium color--error text-center mt-xs">{sendVerificationError}</p>}
                        </Fragment>
                    }
                    {/* <p className="text-center color--txt-tertiary mt-lg">
                        Don't have an account?&nbsp;
                        <button className="link link--primary" onClick={goToSignUp}>Create account</button>
                    </p> */}
                    {isGuestSessionAllowed() &&
                        <div className="auth__view__sign_in__guest_sec">
                            <p className="text-center color--txt-tertiary">Want to explore platform as a Guest?</p>
                            <p className="text-sm text-center color--txt-disabled mb-md">Note: Data will be lost if you lose the session.</p>
                            <button className="btn btn--md btn--radius-sm btn--secondary" onClick={handleGuestProceed}>
                                <span>{!guestProcessing ? 'Continue as a Guest' : 'Creating Guest Session'}</span>
                                {guestProcessing && <span className="btn-loader" />}
                            </button>
                            {guestError && <p className="text-sm text-medium text-center color--error mt-xs">{guestError}</p>}
                        </div>
                    }
                </Fragment>
                : <div className="auth__view__sign_in__resent_verification">
                    <div className="auth__view__sign_in__resent_verification__svg_cont">
                        <MailCheck />
                    </div>
                    <p className="text-3xl text-center text-bold color--primary mt-3xl">Verification Link Sent</p>
                    <p className="text-center color--txt-secondary mb-md">
                        We have emailed you verification link. Please check your inbox to activate your account.
                    </p>
                    <p className="color--error text-xs mb-xl">
                        Please check the spam folder in case you don't see the email in inbox.
                    </p>
                    <button className="btn btn--md btn--radius-sm btn--primary" onClick={handleGoToSignInFromSentVerification}>
                        Go to Sign In
                    </button>
                </div>
            }
        </div>
    );
};

const SignUpView = () => {
    const navigate = useNavigate();

    const [email, setEmail] = useState<string>('');
    const [username, setUsername] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const [emailOrUsername, setEmailOrUsername] = useState<'email' | 'username'>('email');
    const [processing, setProcessing] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<boolean>(false);
    const [redirectCountdown, setRedirectCountdown] = useState<number>(5);
    const [guestProcessing, setGuestProcessing] = useState<boolean>(false);
    const [guestError, setGuestError] = useState<string | null>(null);

    const { mutate: signUp } = useSignup();
    const { mutate: createGuestSession } = useCreateGuestSession();

    const isSubmitDisabled = (): boolean => {
        if (
            !password?.trim()?.length ||
            (emailOrUsername === "email" && !email?.trim()?.length) ||
            (emailOrUsername === "username" && !username?.trim()?.length)
        ) {
            return true;
        };

        return false;
    };

    const handleSubmit = (e: SyntheticEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (isSubmitDisabled() || processing || guestProcessing) return;

        let tokenOf: 'session' | 'guest' = 'guest';
        let token = CookiesHelper.get(AppConfig.cookieTokenNames.guest);
        if (!token) {
            token = CookiesHelper.get(AppConfig.cookieTokenNames.session);
            tokenOf = 'session';
        };
        if (!token) return;

        signUp({
            payload: {
                email: email?.trim()?.length && emailOrUsername === 'email' ? email : null,
                username: username?.trim()?.length && emailOrUsername === 'username' ? username : null,
                password,
                tokenOf,
            },
            token,
            setProcessing,
            setError,
            setSuccess,
            emailOrUsername,
            setRedirectCountdown,
        });
    };

    const handleSwitchEmailOrUsername = () => {
        if (processing || guestProcessing) return;
        setEmailOrUsername(emailOrUsername === 'email' ? 'username' : 'email');
    };

    const goToSignIn = () => {
        if (processing || guestProcessing) return;
        navigate('/auth/sign-in');
    };

    useEffect(() => {
        if (redirectCountdown === 0) {
            window.location.href = '/';
        };
    }, [redirectCountdown]);

    const handleGuestProceed = () => {
        if (!isGuestSessionAllowed() || emailOrUsername !== 'email') return;

        createGuestSession({
            setProcessing: setGuestProcessing,
            setError: setGuestError,
        });
    };

    return (
        <div className="auth__view__sign_up">
            {!success
                ? <Fragment>
                    <p className="text-4xl text-bold color--primary">Create Account</p>
                    <p className="text-lg color--txt-secondary mt-lg">Join us to get started</p>
                    <div className="auth__view__sign_up__social_btns">
                        <button className="btn btn--lg btn--radius-md btn--fluid btn--secondary" disabled>
                            <img src="/assets/google-icon.svg" alt="Google Ico" />
                            <span>Continue with Google</span>
                        </button>
                        <div className="auth__view__sign_up__social_btns__separator">
                            <div /><p>or</p><div />
                        </div>
                    </div>
                    <form className="auth__view__sign_up__form" onSubmit={handleSubmit}>
                        {emailOrUsername === 'email' &&
                            <Fragment>
                                <div className="auth__view__sign_up__form__form_group">
                                    <label htmlFor="sign_up__email">
                                        <p className="text-sm text-medium color--primary">Email Address</p>
                                    </label>
                                    <TextInput
                                        id="sign_up__email"
                                        type="email"
                                        icon={{ icon: <Mail />, position: 'prefix' }}
                                        placeholder="Enter your email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                    />
                                </div>
                                <p className="text-sm text-right color--txt-tertiary mt-xs">
                                    Want to keep identity private?&nbsp;
                                    <button type="button" className="link link--primary text-sm" onClick={handleSwitchEmailOrUsername}>Create username instead</button>
                                </p>
                            </Fragment>
                        }
                        {emailOrUsername === 'username' &&
                            <Fragment>
                                <div className="auth__view__sign_up__form__form_group">
                                    <label htmlFor="sign_up__username">
                                        <p className="text-sm text-medium color--primary">Create Username</p>
                                    </label>
                                    <TextInput
                                        id="sign_up__username"
                                        type="text"
                                        icon={{ icon: <UserPen />, position: 'prefix' }}
                                        placeholder="Create username"
                                        value={username}
                                        onChange={(e) => setUsername(e.target.value)}
                                    />
                                </div>
                                <p className="text-sm text-right color--txt-tertiary mt-xs">
                                    Want to use your email address?&nbsp;
                                    <button type="button" className="link link--primary text-sm" onClick={handleSwitchEmailOrUsername}>Switch to email</button>
                                </p>
                            </Fragment>
                        }
                        <div className="auth__view__sign_up__form__form_group my-lg">
                            <label htmlFor="sign_up__password">
                                <p className="text-sm text-medium color--primary">Password</p>
                            </label>
                            <TextInput
                                id="sign_up__password"
                                type="password"
                                icon={{ icon: <Lock />, position: 'prefix' }}
                                placeholder="Generate a strong password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                            />
                        </div>
                        <button
                            type="submit"
                            className="btn btn--lg btn--radius-sm btn--fluid btn--primary mt-xl"
                            disabled={isSubmitDisabled()}
                        >
                            {processing ? <span className="btn-loader" /> : "Create Account"}
                        </button>
                        {error && <p className="text-sm text-medium color--error text-center mt-xs">{error}</p>}
                    </form>
                    <p className="text-center color--txt-tertiary mt-lg">
                        Already have an account?&nbsp;
                        <button className="link link--primary" onClick={goToSignIn}>Sign in</button>
                    </p>
                </Fragment>
                : <div className="auth__view__sign_up__success">
                    <div className="auth__view__sign_up__success__svg_cont">
                        <UserRoundCheck />
                    </div>
                    <p className="text-3xl text-center text-bold color--primary mt-3xl">Sign up Successful</p>
                    <p className="text-center color--txt-tertiary mb-md">
                        Your account has been successfully created.
                    </p>
                    {
                        emailOrUsername === 'email'
                            ? <Fragment>
                                <p className="text-center color--txt-secondary mb-md">
                                    We have emailed you verification link on {email}. Please check your inbox to activate your account.
                                </p>
                                <p className="color--error text-xs mb-xl">
                                    Please check the spam folder in case you don't see the email in inbox.
                                </p>
                            </Fragment>
                            : <p className="text-center color--txt-secondary mb-md">
                                You will be redirected to home page in {redirectCountdown} second{redirectCountdown > 1 ? 's' : ''}
                            </p>
                    }
                    {emailOrUsername === 'email' && (
                        <button className="btn btn--md btn--radius-sm btn--primary" onClick={goToSignIn}>
                            Go to Sign In
                        </button>
                    )}
                    {isGuestSessionAllowed() && emailOrUsername === 'email' && (
                        <div className="auth__view__sign_up__success__guest_sec">
                            <p className="text-center color--txt-tertiary">Don't have access to inbox right now? You can still explore our platform as a guest.</p>
                            <p className="text-sm text-center color--txt-disabled mb-sm">Note: Data will be lost if you lose the session.</p>
                            <button className="btn btn--md btn--radius-sm btn--secondary" onClick={handleGuestProceed}>
                                <span>{!guestProcessing ? 'Continue as a Guest' : 'Creating Guest Session'}</span>
                                {guestProcessing && <span className="btn-loader" />}
                            </button>
                            {guestError && <p className="text-sm text-medium text-center color--error mt-xs">{guestError}</p>}
                        </div>
                    )}
                </div>
            }
        </div>
    );
};

const ForgotPasswordView = () => {
    const navigate = useNavigate();

    const [emailOrUsername, setEmailOrUsername] = useState<'email' | 'username'>('email');
    const [email, setEmail] = useState<string>('');
    const [username, setUsername] = useState<string>('');
    const [processing, setProcessing] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<boolean>(false);

    const { mutate: sendPasswordResetEmail } = useSendPasswordResetEmail();

    const handleSwitchEmailOrUsername = () => {
        if (processing) return;
        setEmailOrUsername(emailOrUsername === 'email' ? 'username' : 'email');
    };

    const goToSignIn = () => {
        if (processing) return;
        navigate('/auth/sign-in');
    };

    const isSubmitDisabled = (): boolean => {
        if (
            (emailOrUsername === "email" && !email?.trim()?.length) ||
            (emailOrUsername === "username" && !username?.trim()?.length)
        ) {
            return true;
        };

        return false;
    };

    const handleSubmit = (e: SyntheticEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (isSubmitDisabled() || processing) return;

        let tokenOf: 'session' | 'guest' = 'guest';
        let token = CookiesHelper.get(AppConfig.cookieTokenNames.guest);
        if (!token) {
            token = CookiesHelper.get(AppConfig.cookieTokenNames.session);
            tokenOf = 'session';
        };
        if (!token) return;

        sendPasswordResetEmail({
            payload: {
                email: email?.trim()?.length && emailOrUsername === 'email' ? email : null,
                username: username?.trim()?.length && emailOrUsername === 'username' ? username : null,
                tokenOf,
            },
            token,
            setProcessing,
            setError,
            setSuccess,
        });
    };

    return (
        <div className="auth__view__forgot_password">
            {!success
                ? <Fragment>
                    <p className="text-4xl text-bold color--primary">Reset Password</p>
                    <p className="text-lg color--txt-secondary mt-lg">Enter your email and we'll send you reset instructions</p>
                    <form className="auth__view__forgot_password__form" onSubmit={handleSubmit}>
                        {emailOrUsername === 'email' &&
                            <Fragment>
                                <div className="auth__view__forgot_password__form__form_group">
                                    <label htmlFor="forgot_password__email">
                                        <p className="text-sm text-medium color--primary">Email Address</p>
                                    </label>
                                    <TextInput
                                        id="forgot_password__email"
                                        type="email"
                                        icon={{ icon: <Mail />, position: 'prefix' }}
                                        placeholder="Enter your email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                    />
                                </div>
                                <p className="text-sm text-right color--txt-tertiary mt-xs">
                                    Want to use username?&nbsp;
                                    <button type="button" className="link link--primary text-sm" onClick={handleSwitchEmailOrUsername}>Click here</button>
                                </p>
                            </Fragment>
                        }
                        {emailOrUsername === 'username' &&
                            <Fragment>
                                <div className="auth__view__forgot_password__form__form_group">
                                    <label htmlFor="forgot_password__username">
                                        <p className="text-sm text-medium color--primary">Username</p>
                                    </label>
                                    <TextInput
                                        id="forgot_password__username"
                                        type="text"
                                        icon={{ icon: <UserPen />, position: 'prefix' }}
                                        placeholder="Enter your username"
                                        value={username}
                                        onChange={(e) => setUsername(e.target.value)}
                                    />
                                </div>
                                <p className="text-sm text-right color--txt-tertiary mt-xs">
                                    Want to use email?&nbsp;
                                    <button type="button" className="link link--primary text-sm" onClick={handleSwitchEmailOrUsername}>Switch to email</button>
                                </p>
                            </Fragment>
                        }
                        <button
                            type="submit"
                            className="btn btn--lg btn--radius-sm btn--fluid btn--primary mt-xl"
                            disabled={isSubmitDisabled()}
                        >
                            {processing ? <span className="btn-loader" /> : "Send Reset Link"}
                        </button>
                        {error && <p className="text-sm text-medium color--error text-center mt-xs">{error}</p>}
                    </form>
                    <p className="text-center color--txt-tertiary mt-lg">
                        Sign In instead?&nbsp;
                        <button className="link link--primary" onClick={goToSignIn}>Back to Sign in</button>
                    </p>
                </Fragment>
                : <div className="auth__view__forgot_password__sent_reset_link">
                    <div className="auth__view__forgot_password__sent_reset_link__svg_cont">
                        <MailCheck />
                    </div>
                    <p className="text-3xl text-center text-bold color--primary mt-3xl">Password Reset Link Sent</p>
                    <p className="text-center color--txt-secondary mb-md">
                        We have emailed you the password reset link. Please check your inbox to reset your password.
                    </p>
                    <p className="color--error text-xs mb-xl">
                        Please check the spam folder in case you don't see the email in inbox.
                    </p>
                    <button className="btn btn--md btn--radius-sm btn--primary" onClick={goToSignIn}>
                        Go to Sign In
                    </button>
                </div>
            }
        </div>
    );
};

const VerifyEmailView = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get("act_token");

    const [processing, setProcessing] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<boolean>(false);
    const [countdown, setCountdown] = useState<number>(5);

    const { mutate: verifyEmail } = useVerifyEmail();

    useEffect(() => {
        if (token) {
            verifyEmail({
                token,
                setProcessing,
                setError,
                setSuccess,
            });
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    useEffect(() => {
        if (!success) return;

        const interval = setInterval(() => {
            setCountdown((prev) => prev - 1);
        }, 1000);

        return () => clearInterval(interval);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [success]);

    useEffect(() => {
        if (countdown > 0) return;
        navigate('/');

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [countdown]);

    return (
        <div className='auth__view__verify_email'>
            {processing && <p className='text-2xl text-semibold color--primary text-center'>Verifying email.. Please wait..</p>}
            {error && <p className='text-xl color--error text-center text-medium'>{error}</p>}
            {success && <p className='text-xl color--txt-tertiary text-center text-medium'>Account verification successful. Redirecting you to the Home page in <strong>{countdown}</strong> second{countdown > 1 ? 's' : ''}</p>}
        </div>
    );
};

const ResetPasswordView = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get("reset_token");

    const [password, setPassword] = useState('');
    const [confPassword, setConfPassword] = useState('');
    const [processing, setProcessing] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<boolean>(false);
    const [countdown, setCountdown] = useState<number>(5);

    const { mutate: resetPassword } = useResetPassword();

    const isSubmitDisabled = (): boolean => {
        if (!password?.trim()?.length && !confPassword?.trim()?.length) {
            return true;
        };

        return false;
    };

    const handleSubmit = (e: SyntheticEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (isSubmitDisabled() || processing || !token) return;

        if (password !== confPassword) {
            setError("Passwords are not matching.");
            return;
        };

        resetPassword({
            payload: {
                password,
            },
            token,
            setProcessing,
            setError,
            setSuccess,
        });
    };

    useEffect(() => {
        if (!success) return;

        const interval = setInterval(() => {
            setCountdown((prev) => prev - 1);
        }, 1000);

        return () => clearInterval(interval);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [success]);

    useEffect(() => {
        if (countdown > 0) return;
        navigate('/auth/sign-in', { replace: true });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [countdown]);

    return (
        <div className='auth__view__reset_password'>
            {!success
                ? <Fragment>
                    <p className="text-4xl text-bold color--primary">Reset Your Password</p>
                    <p className="text-lg color--txt-secondary mt-lg">Create a strong password to access your account</p>
                    <form className="auth__view__reset_password__form" onSubmit={handleSubmit}>
                        <div className="auth__view__reset_password__form__form_group">
                            <label htmlFor="password">
                                <p className="text-sm text-medium color--primary">Password</p>
                            </label>
                            <TextInput
                                id="password"
                                type="password"
                                icon={{ icon: <Lock />, position: 'prefix' }}
                                placeholder="Create password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                            />
                        </div>
                        <div className="auth__view__reset_password__form__form_group my-lg">
                            <label htmlFor="conf_password">
                                <p className="text-sm text-medium color--primary">Confirm Password</p>
                            </label>
                            <TextInput
                                id="conf_password"
                                type="password"
                                icon={{ icon: <Lock />, position: 'prefix' }}
                                placeholder="Retype password"
                                value={confPassword}
                                onChange={(e) => setConfPassword(e.target.value)}
                            />
                        </div>
                        <button
                            type="submit"
                            className="btn btn--lg btn--radius-sm btn--fluid btn--primary mt-xl"
                            disabled={isSubmitDisabled()}
                        >
                            {processing ? <span className="btn-loader" /> : "Reset Password"}
                        </button>
                        {error && <p className="text-sm text-medium color--error text-center mt-xs">{error}</p>}
                    </form>
                </Fragment>
                : <div className="auth__view__reset_password__success">
                    <div className="auth__view__reset_password__success__svg_cont">
                        <UserRoundCheck />
                    </div>
                    <p className="text-3xl text-center text-bold color--primary mt-3xl">Password Reset Successful</p>
                    <p className="text-center color--txt-tertiary mb-md">
                        Your password has been successfully updated. Please use your new password to Sign In.
                    </p>
                    <p className="text-lg text-center color--txt-tertiary text-medium">
                        You will be redirected to sign in page in <strong>{countdown}</strong> second{countdown > 1 ? 's' : ''}
                    </p>
                </div>
            }
        </div>
    );
};

const SetPasswordView = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get("sp_token");

    const [password, setPassword] = useState('');
    const [confPassword, setConfPassword] = useState('');
    const [processing, setProcessing] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<boolean>(false);
    const [countdown, setCountdown] = useState<number>(5);

    const { mutate: setPasswordMutation } = useSetPassword();

    const isSubmitDisabled = (): boolean => {
        if (!password?.trim()?.length && !confPassword?.trim()?.length) {
            return true;
        };

        return false;
    };

    const handleSubmit = (e: SyntheticEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (isSubmitDisabled() || processing || !token) return;

        if (password !== confPassword) {
            setError("Passwords are not matching.");
            return;
        };

        setPasswordMutation({
            payload: {
                password,
            },
            token,
            setProcessing,
            setError,
            setSuccess,
        });
    };

    useEffect(() => {
        if (!success) return;

        const interval = setInterval(() => {
            setCountdown((prev) => prev - 1);
        }, 1000);

        return () => clearInterval(interval);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [success]);

    useEffect(() => {
        if (countdown > 0) return;
        navigate('/auth/sign-in', { replace: true });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [countdown]);

    return (
        <div className='auth__view__set_password'>
            {!success
                ? <Fragment>
                    <p className="text-4xl text-bold color--primary">Set Your Password</p>
                    <p className="text-lg color--txt-secondary mt-lg">Set a strong password to access your account</p>
                    <form className="auth__view__set_password__form" onSubmit={handleSubmit}>
                        <div className="auth__view__set_password__form__form_group">
                            <label htmlFor="password">
                                <p className="text-sm text-medium color--primary">Password</p>
                            </label>
                            <TextInput
                                id="password"
                                type="password"
                                icon={{ icon: <Lock />, position: 'prefix' }}
                                placeholder="Create password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                            />
                        </div>
                        <div className="auth__view__set_password__form__form_group my-lg">
                            <label htmlFor="conf_password">
                                <p className="text-sm text-medium color--primary">Confirm Password</p>
                            </label>
                            <TextInput
                                id="conf_password"
                                type="password"
                                icon={{ icon: <Lock />, position: 'prefix' }}
                                placeholder="Retype password"
                                value={confPassword}
                                onChange={(e) => setConfPassword(e.target.value)}
                            />
                        </div>
                        <button
                            type="submit"
                            className="btn btn--lg btn--radius-sm btn--fluid btn--primary mt-xl"
                            disabled={isSubmitDisabled()}
                        >
                            {processing ? <span className="btn-loader" /> : "Set Password"}
                        </button>
                        {error && <p className="text-sm text-medium color--error text-center mt-xs">{error}</p>}
                    </form>
                </Fragment>
                : <div className="auth__view__set_password__success">
                    <div className="auth__view__set_password__success__svg_cont">
                        <UserRoundCheck />
                    </div>
                    <p className="text-3xl text-center text-bold color--primary mt-3xl">Password Set Successful</p>
                    <p className="text-center color--txt-tertiary mb-md">
                        Your password has been successfully set. Please use your new password to Sign In.
                    </p>
                    <p className="text-lg text-center color--txt-tertiary text-medium">
                        You will be redirected to sign in page in <strong>{countdown}</strong> second{countdown > 1 ? 's' : ''}
                    </p>
                </div>
            }
        </div>
    );
};